import { useCallback, useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import Modal, { ModalActions } from "@/common/Modal";
import Button from "@/common/Button";
import Markdown from "@/common/Markdown";
import Loading from "@/common/Loading";
import { useAcceptTermsMutation } from "@/services/apis/api.ts";
import { OrganizationModel } from "@/model/Organization";
import {
    TERMS_DOCUMENTS,
    TermsDocumentKey,
    termsDocumentUrl,
} from "./termsDocuments";

/**
 * Blocking modal shown to org admins/owners when the organization has not yet
 * accepted the current terms version. The documents themselves are fetched from
 * the CDN; accepting records the acceptance against the organization.
 */
export default function AcceptTermsModal({
    version,
}: {
    version: OrganizationModel["currentTermsVersion"];
}) {
    const [activeKey, setActiveKey] = useState<TermsDocumentKey>(
        TERMS_DOCUMENTS[0].key,
    );
    const [docs, setDocs] = useState<Partial<Record<TermsDocumentKey, string>>>(
        {},
    );
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [hasMoreBelow, setHasMoreBelow] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [acceptTerms, { isLoading: isAccepting }] = useAcceptTermsMutation();

    const activeDoc = TERMS_DOCUMENTS.find((doc) => doc.key === activeKey)!;
    const content = docs[activeKey];

    // Show a "scroll for more" hint while there is content below the fold.
    const updateScrollHint = useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        setHasMoreBelow(remaining > 8);
    }, []);

    // Re-evaluate when the rendered document changes (tab switch / load).
    useEffect(() => {
        scrollRef.current?.scrollTo({ top: 0 });
        updateScrollHint();
    }, [content, updateScrollHint]);

    useEffect(() => {
        if (content !== undefined) {
            return;
        }

        const controller = new AbortController();
        setIsLoading(true);
        setHasError(false);

        fetch(termsDocumentUrl(version, activeDoc.file), {
            signal: controller.signal,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                return response.text();
            })
            .then((text) => {
                setDocs((prev) => ({ ...prev, [activeKey]: text }));
                setIsLoading(false);
            })
            .catch((error) => {
                if (error.name === "AbortError") {
                    return;
                }
                setHasError(true);
                setIsLoading(false);
            });

        return () => {
            controller.abort();
        };
    }, [activeKey, activeDoc.file, version, content]);

    return (
        <Modal
            open={true}
            // Intentionally non-dismissable: the user must accept to continue.
            onClose={() => {}}
            size="3xl"
            title="Our terms have been updated"
            description="Please review and accept our latest terms to continue using Spectacle."
        >
            <div className="flex flex-wrap gap-2 border-b border-zinc-950/5 pb-3 dark:border-white/10">
                {TERMS_DOCUMENTS.map((doc) => (
                    <button
                        key={doc.key}
                        type="button"
                        onClick={() => setActiveKey(doc.key)}
                        className={clsx(
                            "rounded-lg px-3 py-1.5 text-sm font-medium",
                            doc.key === activeKey
                                ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-300"
                                : "text-subtle hover:bg-zinc-100 dark:hover:bg-white/5",
                        )}
                    >
                        {doc.label}
                    </button>
                ))}
            </div>

            <div className="relative mt-4 border-b border-zinc-950/5 dark:border-white/10">
                <div
                    ref={scrollRef}
                    onScroll={updateScrollHint}
                    className="h-[50vh] overflow-y-auto px-4 py-4"
                >
                    {content !== undefined ? (
                        <Markdown>{content}</Markdown>
                    ) : isLoading ? (
                        <div className="flex h-full items-center justify-center">
                            <Loading />
                        </div>
                    ) : hasError ? (
                        <p className="text-sm text-subtle">
                            We couldn&apos;t load this document. Please try
                            again later.
                        </p>
                    ) : null}
                </div>

                <div
                    className={clsx(
                        "pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-3 transition-opacity duration-200",
                        hasMoreBelow ? "opacity-100" : "opacity-0",
                    )}
                >
                    <span className="flex items-center gap-1 rounded-full bg-zinc-900/90 px-3 py-1 text-xs font-medium text-white shadow-sm backdrop-blur-sm dark:bg-zinc-700/90">
                        <ChevronDownIcon className="size-4 animate-bounce" />
                        Scroll to read more
                    </span>
                </div>
            </div>

            <ModalActions>
                <Button
                    type="primary"
                    loading={isAccepting}
                    onClick={() => {
                        void acceptTerms({ version });
                    }}
                >
                    Accept all
                </Button>
            </ModalActions>
        </Modal>
    );
}
